import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import TipDialog from "../components/TipDialog";
import { Pencil, Plus, X, Link as LinkIcon, ExternalLink, Heart, Sparkles, Crown, BadgeCheck, Star, Mail, UserPlus, UserMinus, Trash2, Ban, Gift, Copy, Rocket } from "lucide-react";
import PushToggle from "../components/PushToggle";

const TIER = {
    pod: { label: "Pod Member", cls: "bg-[#FFB8D1]", Icon: Sparkles },
    elite: { label: "Sole Elite · Verified", cls: "bg-[#FFE55C]", Icon: Crown },
};

export default function Profile() {
    const { username } = useParams();
    const { user, setUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [bio, setBio] = useState("");
    const [links, setLinks] = useState([]);
    const [saving, setSaving] = useState(false);
    const [tipOpen, setTipOpen] = useState(false);
    const [tab, setTab] = useState("posts");
    const [ratingsGiven, setRatingsGiven] = useState([]);
    const [digest, setDigest] = useState(null);
    const [followBusy, setFollowBusy] = useState(false);
    const [blockBusy, setBlockBusy] = useState(false);
    const [applyingBoost, setApplyingBoost] = useState(null);
    const [badges, setBadges] = useState([]);

    const isOwn = user && profile && user.id === profile.id;
    const tierInfo = profile && TIER[profile.tier];

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [u, p] = await Promise.all([
                api.get(`/users/${username}`),
                api.get(`/posts?username=${username}`),
            ]);
            setProfile(u.data);
            setPosts(p.data);
            setBio(u.data.bio || "");
            setLinks(u.data.links || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [username]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (username) api.get(`/users/${username}/ratings-given`).then((r) => setRatingsGiven(r.data)).catch(() => setRatingsGiven([]));
    }, [username]);

    useEffect(() => {
        if (username) api.get(`/users/${username}/badges`).then((r) => setBadges(r.data)).catch(() => setBadges([]));
    }, [username]);

    useEffect(() => {
        if (isOwn) api.get("/digest/me").then((r) => setDigest(r.data)).catch(() => setDigest(null));
    }, [isOwn]);

    const saveProfile = async () => {
        setSaving(true);
        try {
            const clean = links.filter((l) => l.title.trim() && l.url.trim());
            const { data } = await api.patch("/profile", { bio, links: clean });
            setProfile(data);
            setUser({ ...user, ...data });
            setEditing(false);
            toast.success("Profile updated");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <main className="max-w-3xl mx-auto p-8 font-bold text-[#4A4A4A]">Loading…</main>;
    if (!profile) return <main className="max-w-3xl mx-auto p-8">User not found</main>;

    const totalRatings = posts.reduce((a, p) => a + p.rating_count, 0);
    const overall = posts.length
        ? (posts.reduce((a, p) => a + p.avg_rating * p.rating_count, 0) / Math.max(totalRatings, 1))
        : 0;

    return (
        <main className="max-w-3xl mx-auto px-4 py-8">
            <div className="bg-white brut-static rounded-xl p-5 sm:p-8 mb-6" data-testid="profile-card">
                <div className="flex items-start gap-4 mb-4">
                    <div className="w-20 h-20 rounded-full bg-[#FFB8D1] border-2 border-[#0A0A0A] flex items-center justify-center font-black text-3xl shrink-0">
                        {profile.username[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h1 className="display text-3xl sm:text-4xl font-black leading-tight flex items-center gap-2 flex-wrap">
                            @{profile.username}
                            {tierInfo && (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-2 border-[#0A0A0A] text-xs font-black uppercase ${tierInfo.cls}`} data-testid="profile-tier-badge">
                                    <tierInfo.Icon className="w-3 h-3" /> {tierInfo.label}
                                </span>
                            )}
                            {profile.custom_badge && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-2 border-[#0A0A0A] text-xs font-black uppercase bg-[#B8E1FF]" data-testid="profile-custom-badge">
                                    <BadgeCheck className="w-3 h-3" /> Custom
                                </span>
                            )}
                        </h1>
                        {!editing && profile.bio && <p className="mt-1 text-sm font-medium text-[#4A4A4A]">{profile.bio}</p>}
                    </div>
                    {isOwn && !editing && (
                        <button
                            onClick={() => setEditing(true)}
                            className="px-3 py-2 rounded-lg brut bg-[#FFE55C] font-bold text-sm inline-flex items-center gap-1"
                            data-testid="edit-profile-btn"
                        >
                            <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                    )}
                    {!isOwn && user && (
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                onClick={async () => {
                                    setFollowBusy(true);
                                    try {
                                        if (profile.is_following) {
                                            await api.delete(`/users/${profile.username}/follow`);
                                            setProfile({ ...profile, is_following: false, followers_count: Math.max(0, (profile.followers_count || 1) - 1) });
                                            toast.success(`Unfollowed @${profile.username}`);
                                        } else {
                                            await api.post(`/users/${profile.username}/follow`);
                                            setProfile({ ...profile, is_following: true, followers_count: (profile.followers_count || 0) + 1 });
                                            toast.success(`Following @${profile.username}`);
                                        }
                                    } catch (e) { toast.error(formatApiError(e)); }
                                    finally { setFollowBusy(false); }
                                }}
                                disabled={followBusy || profile.is_blocked}
                                className={`px-3 py-2 rounded-lg brut font-bold text-sm inline-flex items-center gap-1 ${profile.is_following ? "bg-white" : "bg-[#0A0A0A] text-white"} disabled:opacity-50`}
                                data-testid="follow-btn"
                            >
                                {profile.is_following ? <><UserMinus className="w-3.5 h-3.5" /> Unfollow</> : <><UserPlus className="w-3.5 h-3.5" /> Follow</>}
                            </button>
                            <button
                                onClick={() => setTipOpen(true)}
                                disabled={profile.is_blocked}
                                className="px-3 py-2 rounded-lg brut bg-[#FFB8D1] font-bold text-sm inline-flex items-center gap-1 disabled:opacity-50"
                                data-testid="tip-user-btn"
                            >
                                <Heart className="w-3.5 h-3.5" /> Tip
                            </button>
                            <button
                                onClick={async () => {
                                    const verb = profile.is_blocked ? "Unblock" : "Block";
                                    if (!window.confirm(`${verb} @${profile.username}?`)) return;
                                    setBlockBusy(true);
                                    try {
                                        if (profile.is_blocked) {
                                            await api.delete(`/users/${profile.username}/block`);
                                            setProfile({ ...profile, is_blocked: false });
                                            toast.success(`Unblocked @${profile.username}`);
                                        } else {
                                            await api.post(`/users/${profile.username}/block`);
                                            setProfile({ ...profile, is_blocked: true, is_following: false });
                                            toast.success(`Blocked @${profile.username}`);
                                        }
                                    } catch (e) { toast.error(formatApiError(e)); }
                                    finally { setBlockBusy(false); }
                                }}
                                disabled={blockBusy}
                                className="px-3 py-2 rounded-lg brut-inset bg-white font-bold text-sm inline-flex items-center gap-1 hover:bg-[#FF5A5F] hover:text-white"
                                data-testid="block-btn"
                            >
                                <Ban className="w-3.5 h-3.5" /> {profile.is_blocked ? "Unblock" : "Block"}
                            </button>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="brut-inset bg-[#FFFDF7] rounded-lg p-3 text-center">
                        <div className="text-2xl font-black">{posts.length}</div>
                        <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Posts</div>
                    </div>
                    <div className="brut-inset bg-[#FFFDF7] rounded-lg p-3 text-center" data-testid="followers-stat">
                        <div className="text-2xl font-black">{profile.followers_count || 0}</div>
                        <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Followers</div>
                    </div>
                    <div className="brut-inset bg-[#FFE55C] rounded-lg p-3 text-center">
                        <div className="text-2xl font-black">{overall.toFixed(1)}</div>
                        <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Avg</div>
                    </div>
                </div>

                {isOwn && (
                    <div className="flex justify-end mb-3">
                        <PushToggle />
                    </div>
                )}

                {isOwn && profile.referral_code && (
                    <div className="brut-inset bg-[#B8E1FF] rounded-lg p-4 mb-4" data-testid="referral-panel">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-white border-2 border-[#0A0A0A] flex items-center justify-center shrink-0">
                                <Gift className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] font-black uppercase tracking-widest text-[#0A0A0A]/70">Invite & earn</div>
                                <div className="font-black text-sm">
                                    Both get a free Post Boost when a friend signs up with your code.
                                </div>
                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                    <code className="px-2 py-1 rounded-lg brut-static bg-[#FFE55C] font-black tracking-widest text-sm" data-testid="referral-code">
                                        {profile.referral_code}
                                    </code>
                                    <button
                                        onClick={() => {
                                            const link = `${window.location.origin}/register?ref=${profile.referral_code}`;
                                            navigator.clipboard.writeText(link);
                                            toast.success("Invite link copied");
                                        }}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg brut bg-white font-bold text-xs"
                                        data-testid="copy-referral"
                                    >
                                        <Copy className="w-3 h-3" /> Copy invite link
                                    </button>
                                </div>
                                <div className="text-[11px] font-medium text-[#0A0A0A]/70 mt-2">
                                    You have <b>{user?.boost_credits || 0}</b> boost credit{(user?.boost_credits || 0) === 1 ? "" : "s"} available.
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {editing ? (
                    <div className="space-y-3" data-testid="edit-profile-form">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wide">Bio</label>
                            <textarea
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                rows={2}
                                maxLength={200}
                                className="w-full mt-1 px-3 py-2 rounded-lg brut-inset bg-white font-medium focus:outline-none"
                                data-testid="edit-bio"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wide">Links</label>
                            <div className="space-y-2 mt-1">
                                {links.map((l, i) => (
                                    <div key={i} className="flex gap-2">
                                        <input
                                            placeholder="Title (e.g. Instagram)"
                                            value={l.title}
                                            onChange={(e) => {
                                                const c = [...links]; c[i] = { ...c[i], title: e.target.value }; setLinks(c);
                                            }}
                                            className="w-1/3 px-3 py-2 rounded-lg brut-inset bg-white font-medium focus:outline-none text-sm"
                                            data-testid={`link-title-${i}`}
                                        />
                                        <input
                                            placeholder="https://…"
                                            value={l.url}
                                            onChange={(e) => {
                                                const c = [...links]; c[i] = { ...c[i], url: e.target.value }; setLinks(c);
                                            }}
                                            className="flex-1 px-3 py-2 rounded-lg brut-inset bg-white font-medium focus:outline-none text-sm"
                                            data-testid={`link-url-${i}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setLinks(links.filter((_, j) => j !== i))}
                                            className="px-2 rounded-lg brut-inset bg-white"
                                            aria-label="Remove link"
                                            data-testid={`link-remove-${i}`}
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                {links.length < 8 && (
                                    <button
                                        type="button"
                                        onClick={() => setLinks([...links, { title: "", url: "" }])}
                                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg brut-inset bg-white font-bold text-sm"
                                        data-testid="link-add"
                                    >
                                        <Plus className="w-4 h-4" /> Add link
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={saveProfile}
                                disabled={saving}
                                className="px-4 py-2 rounded-lg brut bg-[#0A0A0A] text-white font-bold disabled:opacity-50"
                                data-testid="save-profile-btn"
                            >
                                {saving ? "Saving…" : "Save"}
                            </button>
                            <button
                                onClick={() => { setEditing(false); setBio(profile.bio || ""); setLinks(profile.links || []); }}
                                className="px-4 py-2 rounded-lg brut-inset bg-white font-bold"
                                data-testid="cancel-edit-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    profile.links && profile.links.length > 0 && (
                        <div className="space-y-2" data-testid="profile-links">
                            {profile.links.map((l, i) => (
                                <a
                                    key={i}
                                    href={l.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between px-4 py-3 rounded-full brut bg-[#B8E1FF] font-bold text-sm"
                                    data-testid={`profile-link-${i}`}
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <LinkIcon className="w-4 h-4" /> {l.title}
                                    </span>
                                    <ExternalLink className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                    )
                )}
            </div>

            {badges.length > 0 && (
                <section className="brut-static bg-white rounded-xl p-4 mb-4" data-testid="badges-section">
                    <div className="text-xs font-black uppercase tracking-wider text-[#4A4A4A] mb-2">Badges</div>
                    <div className="flex flex-wrap gap-2">
                        {badges.map((b) => (
                            <span
                                key={b.key}
                                title={b.desc}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border-2 border-[#0A0A0A] text-xs font-bold ${b.unlocked ? "bg-[#FFE55C]" : "bg-white opacity-40"}`}
                                data-testid={`badge-${b.key}`}
                            >
                                <span className="text-sm">{b.emoji}</span> {b.name}
                            </span>
                        ))}
                    </div>
                </section>
            )}

            <div className="flex items-center gap-2 mb-4" data-testid="profile-tabs">
                <button
                    onClick={() => setTab("posts")}
                    className={`px-4 py-2 rounded-lg border-2 border-[#0A0A0A] font-bold text-sm ${tab === "posts" ? "bg-[#FFB8D1] shadow-[3px_3px_0px_#0A0A0A]" : "bg-white"}`}
                    data-testid="tab-posts"
                >
                    Posts <span className="text-xs text-[#0A0A0A]/60">({posts.length})</span>
                </button>
                <button
                    onClick={() => setTab("ratings")}
                    className={`px-4 py-2 rounded-lg border-2 border-[#0A0A0A] font-bold text-sm ${tab === "ratings" ? "bg-[#FFE55C] shadow-[3px_3px_0px_#0A0A0A]" : "bg-white"}`}
                    data-testid="tab-ratings"
                >
                    <Star className="inline w-3.5 h-3.5 mr-1" />Ratings given <span className="text-xs text-[#0A0A0A]/60">({ratingsGiven.length})</span>
                </button>
                {isOwn && (
                    <button
                        onClick={() => setTab("digest")}
                        className={`px-4 py-2 rounded-lg border-2 border-[#0A0A0A] font-bold text-sm ${tab === "digest" ? "bg-[#B8E1FF] shadow-[3px_3px_0px_#0A0A0A]" : "bg-white"}`}
                        data-testid="tab-digest"
                    >
                        <Mail className="inline w-3.5 h-3.5 mr-1" />Weekly digest
                    </button>
                )}
            </div>

            {tab === "posts" && (posts.length === 0 ? (
                <div className="border-2 border-dashed border-[#0A0A0A] rounded-xl p-8 text-center bg-white/60" data-testid="profile-no-posts">
                    <div className="font-black">No posts yet.</div>
                    {isOwn && <Link to="/upload" className="mt-3 inline-block px-4 py-2 rounded-lg brut bg-[#FFB8D1] font-bold">Post your first</Link>}
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="profile-posts">
                    {posts.map((p) => (
                        <div key={p.id} className="relative">
                            <Link
                                to={`/p/${p.id}`}
                                className="block relative aspect-square rounded-xl overflow-hidden brut-static bg-[#FFFDF7]"
                                data-testid={`profile-post-${p.id}`}
                            >
                                <img src={fileUrl(p.image_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                <div className="absolute bottom-2 right-2 bg-[#FFE55C] border-2 border-[#0A0A0A] rounded-full px-2 py-0.5 text-xs font-black">
                                    {p.avg_rating.toFixed(1)}
                                </div>
                            </Link>
                            {isOwn && (
                                <div className="absolute top-2 right-2 flex flex-col gap-1">
                                    {(user?.boost_credits || 0) > 0 && !p.boosted && (
                                        <button
                                            onClick={async () => {
                                                setApplyingBoost(p.id);
                                                try {
                                                    await api.post(`/posts/${p.id}/apply-boost-credit`);
                                                    toast.success("Boost applied — 24h in Fresh!");
                                                    load();
                                                    if (typeof setUser === "function" && user) {
                                                        setUser({ ...user, boost_credits: Math.max(0, (user.boost_credits || 1) - 1) });
                                                    }
                                                } catch (e) { toast.error(formatApiError(e)); }
                                                finally { setApplyingBoost(null); }
                                            }}
                                            disabled={applyingBoost === p.id}
                                            className="w-8 h-8 rounded-full bg-[#FFE55C] border-2 border-[#0A0A0A] flex items-center justify-center hover:bg-[#0A0A0A] hover:text-[#FFE55C]"
                                            aria-label="Use boost credit"
                                            title="Use a free Post Boost credit"
                                            data-testid={`use-credit-${p.id}`}
                                        >
                                            <Rocket className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                    <button
                                        onClick={async () => {
                                            if (!window.confirm("Delete this post? This cannot be undone.")) return;
                                            try {
                                                await api.delete(`/posts/${p.id}`);
                                                setPosts(posts.filter((x) => x.id !== p.id));
                                                toast.success("Post deleted");
                                            } catch (e) { toast.error(formatApiError(e)); }
                                        }}
                                        className="w-8 h-8 rounded-full bg-white border-2 border-[#0A0A0A] flex items-center justify-center hover:bg-[#FF5A5F] hover:text-white"
                                        aria-label="Delete post"
                                        data-testid={`delete-post-${p.id}`}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ))}

            {tab === "ratings" && (
                ratingsGiven.length === 0 ? (
                    <div className="border-2 border-dashed border-[#0A0A0A] rounded-xl p-8 text-center bg-white/60" data-testid="ratings-empty">
                        <div className="font-black">No ratings yet.</div>
                        <div className="text-sm text-[#4A4A4A] mt-1 font-medium">Start rating posts on the Fresh feed.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="ratings-list">
                        {ratingsGiven.map((r) => (
                            <Link
                                key={r.post_id}
                                to={`/p/${r.post_id}`}
                                className="block relative aspect-square rounded-xl overflow-hidden brut-static bg-[#FFFDF7]"
                                data-testid={`rating-item-${r.post_id}`}
                            >
                                <img src={fileUrl(r.post_image_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                <div className="absolute bottom-2 right-2 bg-[#FFE55C] border-2 border-[#0A0A0A] rounded-full px-2 py-0.5 text-xs font-black">
                                    You: {r.rating}
                                </div>
                                <div className="absolute top-2 left-2 bg-white/90 border-2 border-[#0A0A0A] rounded-full px-2 py-0.5 text-[10px] font-bold">
                                    @{r.post_username}
                                </div>
                            </Link>
                        ))}
                    </div>
                )
            )}

            {tab === "digest" && isOwn && (
                <div className="brut-static bg-white rounded-xl p-6" data-testid="digest-panel">
                    <div className="flex items-center gap-2 mb-3">
                        <Mail className="w-5 h-5" />
                        <h3 className="display text-xl font-black">Your last 7 days</h3>
                        <span className="ml-auto px-2 py-0.5 rounded-full border-2 border-[#0A0A0A] bg-[#FFE55C] text-[10px] font-black uppercase" title="Email sending mocked">
                            Preview
                        </span>
                    </div>
                    {digest ? (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                                <Stat label="New posts" value={digest.new_posts} />
                                <Stat label="Ratings" value={digest.ratings_received} />
                                <Stat label="Comments" value={digest.comments_received} />
                                <Stat label="Tips" value={`$${(digest.tips_received_usd || 0).toFixed(2)}`} />
                            </div>
                            {digest.top_post && (
                                <div className="text-sm">
                                    <div className="text-xs font-black uppercase tracking-wider text-[#4A4A4A] mb-2">Your top post</div>
                                    <Link to={`/p/${digest.top_post.id}`} className="flex items-center gap-3 brut-inset rounded-lg p-2 hover:bg-[#FFFDF7]" data-testid="digest-top-post">
                                        <img src={fileUrl(digest.top_post.image_path)} className="w-16 h-16 rounded-lg object-cover border-2 border-[#0A0A0A]" alt="" />
                                        <div>
                                            <div className="font-black">{digest.top_post.avg_rating.toFixed(1)}/10</div>
                                            <div className="text-xs font-medium text-[#4A4A4A]">{digest.top_post.rating_count} rating{digest.top_post.rating_count === 1 ? "" : "s"}</div>
                                        </div>
                                    </Link>
                                </div>
                            )}
                            <p className="text-[11px] text-[#4A4A4A] font-medium mt-4">
                                <b>MOCKED:</b> Actual email delivery isn't wired up yet — this is what your Sunday digest would look like.
                            </p>
                        </>
                    ) : (
                        <div className="text-sm text-[#4A4A4A] font-medium">Loading digest…</div>
                    )}
                </div>
            )}
            {tipOpen && <TipDialog username={profile.username} onClose={() => setTipOpen(false)} />}
        </main>
    );
}

function Stat({ label, value }) {
    return (
        <div className="brut-inset bg-[#FFFDF7] rounded-lg p-3 text-center">
            <div className="display text-2xl font-black">{value}</div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">{label}</div>
        </div>
    );
}
